/**
 * Service API unifié pour SchoolBus Admin
 * Gère toutes les communications avec le backend
 */

import { API_BASE_URL } from '../config/api.config.js';
import logger from '@/utils/logger';
import {
  getToken as storageGetToken,
  setToken as storageSetToken,
  clearToken as storageClearToken,
  getRefreshToken as storageGetRefreshToken,
} from '@/utils/storage';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  getHeaders() {
    const token = storageGetToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }

  /**
   * Méthode générique pour les requêtes HTTP
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    const config = {
      headers: this.getHeaders(),
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Gestion des erreurs HTTP
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP Error: ${response.status}`);
      }

      const data = await response.json();

      // Vérification du format de réponse SchoolBus
      if (data.success === false) {
        throw new Error(data.message || 'Erreur API');
      }

      return data;
    } catch (error) {
      logger.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  /**
   * Méthodes HTTP de base
   */
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    return this.request(url, {
      method: 'GET',
    });
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  /**
   * Gestion de l'authentification
   */
  setToken(token) {
    if (token) {
      storageSetToken(token, true);
    } else {
      storageClearToken();
    }
  }

  getToken() {
    return storageGetToken();
  }

  clearToken() {
    storageClearToken();
  }

  isAuthenticated() {
    return !!storageGetToken();
  }

  // ==========================================
  // ENDPOINTS SPÉCIFIQUES SCHOOLBUS
  // ==========================================

  /**
   * Authentification
   */
  async login(email, password) {
    const response = await this.post('/auth/login', { email, password });

    if (response.success && response.data.tokens) {
      this.setToken(response.data.tokens.accessToken);
    }

    return response;
  }

  async logout() {
    try {
      await this.post('/auth/logout');
    } catch (error) {
      logger.warn('Erreur lors du logout:', error);
    } finally {
      this.clearToken();
    }
  }

  async refreshToken() {
    const refreshToken = storageGetRefreshToken();
    if (!refreshToken) {
      throw new Error('Aucun refresh token disponible');
    }

    const response = await this.post('/auth/refresh', { refreshToken });

    if (response.success && response.data.tokens) {
      this.setToken(response.data.tokens.accessToken);
    }

    return response;
  }

  /**
   * Health Check
   */
  async getHealth() {
    return this.get('/health');
  }

  /**
   * Dashboard Admin
   */
  async getDashboardStats() {
    return this.get('/admin/dashboard/stats');
  }

  /**
   * Gestion des Écoles
   */
  async getSchools(params = {}) {
    return this.get('/admin/schools', params);
  }

  async getSchool(id) {
    return this.get(`/admin/schools/${id}`);
  }

  async createSchool(data) {
    return this.post('/admin/schools', data);
  }

  async updateSchool(id, data) {
    return this.put(`/admin/schools/${id}`, data);
  }

  async deleteSchool(id) {
    return this.delete(`/admin/schools/${id}`);
  }

  /**
   * Gestion des Utilisateurs
   */
  async getUsers(params = {}) {
    return this.get('/admin/users', params);
  }

  async getUser(id) {
    return this.get(`/admin/users/${id}`);
  }

  async createUser(data) {
    return this.post('/admin/users', data);
  }

  async updateUser(id, data) {
    return this.put(`/admin/users/${id}`, data);
  }

  async deleteUser(id) {
    return this.delete(`/admin/users/${id}`);
  }

  /**
   * Gestion des Bus
   */
  async getBuses(params = {}) {
    return this.get('/admin/buses', params);
  }

  async getBus(id) {
    return this.get(`/admin/buses/${id}`);
  }

  async createBus(data) {
    return this.post('/admin/buses', data);
  }

  async updateBus(id, data) {
    return this.put(`/admin/buses/${id}`, data);
  }

  async deleteBus(id) {
    return this.delete(`/admin/buses/${id}`);
  }

  /**
   * Gestion des Étudiants
   */
  async getStudents(params = {}) {
    return this.get('/admin/students', params);
  }

  async getStudent(id) {
    return this.get(`/admin/students/${id}`);
  }

  async createStudent(data) {
    return this.post('/admin/students', data);
  }

  async updateStudent(id, data) {
    return this.put(`/admin/students/${id}`, data);
  }

  async deleteStudent(id) {
    return this.delete(`/admin/students/${id}`);
  }

  /**
   * Gestion des Circuits
   */
  async getRoutes(params = {}) {
    return this.get('/admin/routes', params);
  }

  async getRoute(id) {
    return this.get(`/admin/routes/${id}`);
  }

  async createRoute(data) {
    return this.post('/admin/routes', data);
  }

  async updateRoute(id, data) {
    return this.put(`/admin/routes/${id}`, data);
  }

  async deleteRoute(id) {
    return this.delete(`/admin/routes/${id}`);
  }

  /**
   * Gestion des Arrêts
   */
  async getStops(params = {}) {
    return this.get('/admin/stops', params);
  }

  async getStop(id) {
    return this.get(`/admin/stops/${id}`);
  }

  async createStop(data) {
    return this.post('/admin/stops', data);
  }

  async updateStop(id, data) {
    return this.put(`/admin/stops/${id}`, data);
  }

  async deleteStop(id) {
    return this.delete(`/admin/stops/${id}`);
  }
}

// Instance singleton
const apiService = new ApiService();

export default apiService;
